import React, { useState } from 'react';
import { SlideFooter } from '../components/canvas/SlideFooter';
import { TITLE_FONT_SIZE_LEVELS, DEFAULT_TITLE_FONT_SIZE, BODY_FONT_SIZE_LEVELS, DEFAULT_BODY_FONT_SIZE, KICKER_FONT_SIZE_LEVELS, DEFAULT_KICKER_FONT_SIZE, SUBTITLE_FONT_SIZE_LEVELS, DEFAULT_SUBTITLE_FONT_SIZE, TAGS_FONT_SIZE_LEVELS, DEFAULT_TAGS_FONT_SIZE } from '../constants/typography';
import { renderFormattedText } from '../utils/formatText';
import { usePreviewInteraction } from '../contexts/PreviewInteractionContext';
import { InlineEditable } from '../components/canvas/InlineEditable';
import { NudgeableGroup } from '../components/canvas/NudgeableGroup';
import { DEFAULT_POLAROID_COLOR } from '../constants/normies';

/** Check if a hex color is dark (for adapting placeholder text) */
function isDark(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return false;
  const lum = (parseInt(m[1], 16) * 299 + parseInt(m[2], 16) * 587 + parseInt(m[3], 16) * 114) / 1000;
  return lum < 128;
}

/**
 * Normies Polaroid layout — Instant-photo frame with caption zone.
 *
 * The Normie sits in the "photo area" of a polaroid frame.
 * Text colors driven by the palette system (pc prop).
 * Generative background visible around the frame.
 *
 * Reads data.normies?.heroImage directly.
 * Reads data.normies?.polaroidColor for frame bg (defaults to off-white).
 *
 * Stats are hidden when mode is 'agent-card' — they don't fit in
 * the polaroid caption area for that layout.
 */
export function NormiesPolaroidLayout({ data, fontPreset, pc }) {
  const { interactive, onElementClick, editingField, onEditChange, onEndEdit } = usePreviewInteraction();
  const [imgLoaded, setImgLoaded] = useState(false);

  const heroImage = data.normies?.heroImage;
  const polaroidColor = data.normies?.polaroidColor || DEFAULT_POLAROID_COLOR;
  const isDarkFrame = polaroidColor && isDark(polaroidColor);
  const titleSize = TITLE_FONT_SIZE_LEVELS[data.titleFontSize ?? DEFAULT_TITLE_FONT_SIZE]?.size || 74;
  const bodySize = BODY_FONT_SIZE_LEVELS[data.bodyFontSize ?? DEFAULT_BODY_FONT_SIZE]?.size || 32;
  const kickerSize = KICKER_FONT_SIZE_LEVELS[data.kickerFontSize ?? DEFAULT_KICKER_FONT_SIZE]?.size || 16;
  const subtitleSize = SUBTITLE_FONT_SIZE_LEVELS[data.subtitleFontSize ?? DEFAULT_SUBTITLE_FONT_SIZE]?.size || 20;
  const tagsSize = TAGS_FONT_SIZE_LEVELS[data.tagsFontSize ?? DEFAULT_TAGS_FONT_SIZE]?.size || 11;

  const handleTitleClick = (e) => {
    if (!interactive || !onElementClick) return;
    if (editingField === 'title') return;
    e.stopPropagation();
    onElementClick('header', 'title');
  };

  const handleBodyClick = (e) => {
    if (!interactive || !onElementClick) return;
    e.stopPropagation();
    onElementClick('text', 'subtitle');
  };

  return (
    <>
      {/* Polaroid frame */}
      <NudgeableGroup groupId="content" className="absolute" style={{
        top: '5%',
        left: '8%',
        right: '8%',
        bottom: '8%',
      }}>
        <div
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: polaroidColor,
            boxShadow: '0 8px 40px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Photo area — square */}
          <div
            className="relative overflow-hidden flex-shrink-0"
            style={{
              margin: '24px 24px 0 24px',
              aspectRatio: '1 / 1',
            }}
          >
            {heroImage ? (
              <img
                src={heroImage}
                alt=""
                className="w-full h-full"
                style={{
                  objectFit: 'cover',
                  imageRendering: 'pixelated',
                  opacity: imgLoaded ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                }}
                onLoad={() => setImgLoaded(true)}
                crossOrigin="anonymous"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center"
                style={{ backgroundColor: isDarkFrame ? 'rgba(255,255,255,0.06)' : '#e3e5e4' }}
              >
                <div className="text-center">
                  <div style={{ fontSize: '48px', lineHeight: 1, opacity: 0.2 }}>👤</div>
                  <div style={{
                    fontFamily: 'IBM Plex Mono, monospace',
                    fontSize: '12px',
                    color: isDarkFrame ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                    marginTop: '8px',
                    letterSpacing: '0.05em',
                  }}>
                    LOAD A NORMIE
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Caption area — bottom portion, tags pinned to bottom */}
          <div style={{ padding: '20px 32px 28px', flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Text content */}
            <div>
            {/* Kicker */}
            {data.kickerEnabled !== false && data.kicker && (
              <div
                style={{
                  fontFamily: fontPreset.subtitle || 'IBM Plex Mono, monospace',
                  fontSize: `${kickerSize}px`,
                  fontWeight: 600,
                  letterSpacing: '0.15em',
                  color: pc.el.kicker || pc.textMuted,
                  textTransform: 'uppercase',
                  marginBottom: '8px',
                }}
              >
                {data.kicker}
              </div>
            )}

            {/* Title — uses palette colors */}
            <InlineEditable
              value={data.title}
              isEditing={editingField === 'title'}
              onChange={(html) => onEditChange && onEditChange('title', html)}
              onBlur={onEndEdit}
              className={`leading-[1.1] w-full${editingField === 'title' ? ' preview-editing' : (interactive ? ' preview-clickable' : '')}`}
              style={{
                fontSize: `${titleSize}px`,
                fontFamily: fontPreset.title,
                fontWeight: fontPreset.titleWeight || 700,
                color: pc.el.title || pc.textPrimary,
                textTransform: 'uppercase',
                letterSpacing: '-0.01em',
              }}
              onClick={handleTitleClick}
            >
              {renderFormattedText(data.title)}
            </InlineEditable>

            {/* Subtitle */}
            {data.subtitleEnabled !== false && data.subtitle && (
              <InlineEditable
                value={data.subtitle || ''}
                isEditing={editingField === 'subtitle'}
                onChange={(text) => onEditChange && onEditChange('subtitle', text)}
                onBlur={onEndEdit}
                plainText
                className={`mt-1${editingField === 'subtitle' ? ' preview-editing' : (interactive ? ' preview-clickable' : '')}`}
                style={{
                  fontFamily: fontPreset.subtitle || 'IBM Plex Mono, monospace',
                  fontSize: `${subtitleSize}px`,
                  color: pc.el.subtitle || pc.textMuted,
                }}
                onClick={handleBodyClick}
              >
                {data.subtitle}
              </InlineEditable>
            )}

            {/* Body */}
            {data.bodyEnabled !== false && data.body && (
              <div
                className="mt-2"
                style={{
                  fontFamily: fontPreset.body || 'IBM Plex Mono, monospace',
                  fontSize: `${bodySize}px`,
                  lineHeight: 1.4,
                  color: pc.el.body || pc.textSubtle,
                }}
              >
                {renderFormattedText(data.body)}
              </div>
            )}

            {/* Tags — pinned to bottom of caption area */}
            </div>
            {data.tagsEnabled !== false && data.meta && data.meta.length > 0 && (
              <div className="flex flex-wrap gap-2" style={{ marginTop: 'auto', paddingTop: '8px' }}>
                {data.meta.map((tag, i) => (
                  <span
                    key={i}
                    style={{
                      fontFamily: 'IBM Plex Mono, monospace',
                      fontSize: `${tagsSize}px`,
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      color: pc.el.tagText || pc.textMuted,
                      border: `1px solid ${pc.el.tagBorder || pc.accent(0.30)}`,
                      padding: '2px 8px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </NudgeableGroup>

      {/* Footer — outside the frame, on the generative background */}
      <div className="absolute bottom-[24px] left-[64px] right-[64px]">
        <SlideFooter data={data} pc={pc} />
      </div>
    </>
  );
}
